/**
 * Configured ingest drop zones from arbitium_sources (replaces arbitium_sources_status).
 */

import { fetchRing } from '../core';

export type SourceStatus = 'active' | 'inactive' | 'error' | string;

export interface SourceItem {
  _id: string;
  name?: string;
  provider?: string;
  status?: SourceStatus;
  dropzone_type?: string;
  bucket?: string;
  prefix?: string;
  prefix_filter?: string;
  region?: string;
  assume_role_arn?: string;
  object_filter?: string;
  normalize_mode?: string;
  adapter_uri?: string;
  candidate_source?: string;
  starter_key?: string;
  last_scanned_at?: string;
  last_error?: string;
  last_status?: string;
  cursor_key?: string;
  [key: string]: unknown;
}

const RING_SOURCES = 'arbitium_sources';

export async function getSources(): Promise<SourceItem[]> {
  const res = await fetchRing<SourceItem>(RING_SOURCES, { all: true });
  return Array.isArray(res.items) ? res.items : [];
}

/** Map provider / name to a /public/sources logo slug when available. */
export function sourceLogoSlug(source: Pick<SourceItem, 'provider' | 'name' | 'candidate_source'>): string {
  const raw = String(source.provider || source.candidate_source || source.name || '')
    .toLowerCase()
    .trim();
  if (!raw) return '';
  if (raw.includes('aws') || raw.includes('cloudtrail')) return 'aws';
  if (raw.includes('okta')) return 'okta';
  if (raw.includes('gcp') || raw.includes('google')) return 'gcp';
  if (raw.includes('azure') || raw.includes('entra') || raw.includes('microsoft')) return 'azure';
  if (raw.includes('crowdstrike') || raw.includes('falcon')) return 'crowdstrike';
  if (raw.includes('palo') || raw.includes('pan-os') || raw.includes('panos')) return 'paloalto';
  if (raw.includes('splunk')) return 'splunk';
  if (raw.includes('fortinet') || raw.includes('forti')) return 'fortinet';
  if (raw.includes('zscaler')) return 'zscaler';
  if (raw.includes('servicenow')) return 'servicenow';
  if (raw.includes('jira')) return 'jira';
  if (raw.includes('checkpoint') || raw.includes('check point')) return 'checkpoint';
  return raw.replace(/[^a-z0-9]+/g, '');
}

export function sourceLogoExtension(slug: string): string {
  return slug === 'checkpoint' ? 'png' : 'svg';
}

export function sourceDisplayName(source: SourceItem): string {
  const name = String(source.name || '').trim();
  if (name) return name;
  const provider = String(source.provider || '').trim();
  if (provider) return provider;
  return source._id || 'Source';
}

export function isSourceActive(source: SourceItem): boolean {
  return String(source.status || '').toLowerCase() === 'active';
}
